const STATUS = {
  UNREVIEWED: 'Unreviewed',
  REVIEWED_WAITING_ORG_OWNER_APPROVAL: 'Reviewed - Awaiting Org Owner Approval',
  REVIEWED_ACTION_REQUIRED: 'Reviewed - Action Required',
  REVIEWED_NO_ACTION_REQUIRED: 'Reviewed - No action required',
  REVIEWED_ESCALATED: 'Reviewed - Escalated',
  AUDIT_REJECT_REVIEWER: 'Audit Rejected - Unreviewed',
  FOCAL_REJECT_REVIEWER: 'Business Rejected - Unreviewed',
  AUDIT_REJECT_ORG: 'Audit Rejected - Awaiting Review by Org-Owner',
  FOCAL_REJECT_ORG: 'Business Rejected - Awaiting Review by Org-Owner',
  ORG_OWNER_REVIEWED: 'Reviewed - No action required',
  ORG_OWNER_ACTION_ASSIGNED: 'Reviewed - Action Assigned',
  ORG_OWNER_ACTION_COMPLETED: 'Reviewed - Action Complete',
  ESCALATED: 'Reviewed - Escalated',
  TRACKING_STATUS_COMPLETED: 'all_complete',
};

const REVIEWED_BY_REVIEWER = [
  STATUS.REVIEWED_WAITING_ORG_OWNER_APPROVAL,
  STATUS.REVIEWED_ACTION_REQUIRED,
  STATUS.REVIEWED_NO_ACTION_REQUIRED,
  STATUS.REVIEWED_ESCALATED,
];

export interface IStatusChecker {
  active: (anomaly: any) => boolean;
  completed: (anomaly: any) => boolean;
  pending?: (anomaly: any) => boolean;
  rejected?: (anomaly: any) => boolean;
  noAction?: (anomaly: any) => boolean;
  projectWithPrev?: (anomaly: any) => boolean;
  projectWithoutPrev?: (anomaly: any) => boolean;
  projectWithPrevLine?: (anomaly: any) => boolean;
  projectWithoutPrevLine?: (anomaly: any) => boolean;
}

export interface IDominoProps {
  anomaly: any;
  fullOpportunityDetail?: any;
}

export interface IStatus extends IStatusChecker {
  label: string;
  className?: string;
}

const reviewedByReviewer = (anomaly: any) =>
  REVIEWED_BY_REVIEWER.includes(anomaly?.card_display_status);
const evidencePending = (anomaly: any) =>
  (reviewer.completed(anomaly) || rejectedAtReviewerLevel(anomaly)) &&
  !anomaly.evidence_uploaded;
const rejectedAtOrgOwnerLevel = (anomaly: any) =>
  anomaly?.card_display_status === STATUS.AUDIT_REJECT_ORG ||
  anomaly?.card_display_status === STATUS.FOCAL_REJECT_ORG;
const rejectedAtReviewerLevel = (anomaly: any) =>
  anomaly?.card_display_status === STATUS.AUDIT_REJECT_REVIEWER ||
  anomaly?.card_display_status === STATUS.FOCAL_REJECT_REVIEWER;
const orgOwnerNoAction = (anomaly: any) =>
  anomaly?.card_display_status === STATUS.REVIEWED_ESCALATED ||
  anomaly?.card_display_status === STATUS.REVIEWED_NO_ACTION_REQUIRED;

const assigned: IStatusChecker = {
  active: (anomaly: any) =>
    !(anomaly?.reviewer_id && anomaly?.orguser_id) ||
    anomaly?.is_orphaned === 'Y',
  completed: (anomaly: any) =>
    anomaly?.reviewer_id && anomaly?.orguser_id && anomaly?.is_orphaned !== 'Y',
};

const checklist: IStatusChecker = {
  active: (anomaly: any) =>
    assigned.completed(anomaly) && !anomaly.checklist_completed,
  completed: (anomaly: any) => anomaly.checklist_completed,
};

const evidence: IStatusChecker = {
  active: (anomaly: any) =>
    assigned.completed(anomaly) &&
    !anomaly.evidence_uploaded &&
    !evidencePending(anomaly),
  pending: evidencePending,
  completed: (anomaly: any) => anomaly.evidence_uploaded,
};

const orgActionAssigned: IStatusChecker = {
  active: (anomaly: any) =>
    reviewer.completed(anomaly) || rejectedAtOrgOwnerLevel(anomaly),
  completed: (anomaly: any) =>
    anomaly?.card_display_status === STATUS.ORG_OWNER_ACTION_ASSIGNED ||
    orgActionCompleted.completed(anomaly),
  rejected: rejectedAtOrgOwnerLevel,
  noAction: orgOwnerNoAction,
};

const reviewer: IStatusChecker = {
  active: (anomaly: any) =>
    assigned.completed(anomaly) &&
    (anomaly?.card_display_status === STATUS.UNREVIEWED ||
      rejectedAtReviewerLevel(anomaly)),
  completed: (anomaly: any) =>
    reviewedByReviewer(anomaly) ||
    orgActionAssigned.completed(anomaly) ||
    orgActionAssigned.rejected(anomaly),
  rejected: rejectedAtReviewerLevel,
  noAction: orgOwnerNoAction,
};

const orgActionCompleted: IStatusChecker = {
  active: (anomaly: any) => orgActionAssigned.completed(anomaly),
  completed: (anomaly: any) =>
    anomaly?.tracking_status === STATUS.TRACKING_STATUS_COMPLETED ||
    anomaly?.card_display_status === STATUS.ORG_OWNER_ACTION_COMPLETED,
  rejected: rejectedAtOrgOwnerLevel,
  noAction: (anomaly: any) =>
    anomaly?.card_display_status === STATUS.REVIEWED_NO_ACTION_REQUIRED,
};

const actionAccepted: IStatusChecker = {
  active: (anomaly: any) => orgActionCompleted.completed(anomaly),
  completed: (anomaly: any) =>
    anomaly?.card_display_status === STATUS.ORG_OWNER_ACTION_COMPLETED,
  noAction: (anomaly: any) =>
    anomaly?.card_display_status === STATUS.REVIEWED_NO_ACTION_REQUIRED,
};

const closed: IStatusChecker = {
  active: (anomaly: any) => orgActionCompleted.completed(anomaly),
  completed: (anomaly: any) =>
    (anomaly?.card_display_status === STATUS.ORG_OWNER_ACTION_COMPLETED &&
      actionAccepted.completed(anomaly)) ||
    anomaly?.card_display_status === STATUS.REVIEWED_NO_ACTION_REQUIRED,
};

const isProject = (anomaly: any) => anomaly?.insight_module_name === 'Project';
const projectWithPrev = (anomaly: any) =>
  isProject(anomaly) && !!anomaly?.previous_anomaly_id;
const projectWithoutPrev = (anomaly: any) =>
  isProject(anomaly) && !anomaly?.previous_anomaly_id;

const DOMINO_STATUS: IStatus[] = [
  {
    label: 'app.anomalydetails.dominostatus.anomalyassigned',
    ...assigned,
    projectWithPrevLine: projectWithPrev,
  },
  {
    label: 'app.anomalydetails.dominostatus.checklistcompleted',
    ...checklist,
    projectWithPrev,
    projectWithPrevLine: projectWithPrev,
    projectWithoutPrev,
  },
  {
    label: 'app.anomalydetails.dominostatus.evidenceuploaded',
    ...evidence,
    projectWithPrev,
    projectWithPrevLine: projectWithPrev,
  },
  {
    label: 'app.anomalydetails.dominostatus.reviewedbyreviewer',
    ...reviewer,
    className: 'dotted',
    projectWithPrev,
    projectWithPrevLine: projectWithPrev,
  },
  {
    label: 'app.anomalydetails.dominostatus.reviewedbyorgowner',
    ...orgActionAssigned,
    className: 'dotted',
    projectWithoutPrev,
    projectWithoutPrevLine: projectWithoutPrev,
  },
  {
    label: 'app.anomalydetails.dominostatus.ownercompletedaction',
    ...orgActionCompleted,
    className: 'dotted',
    projectWithoutPrev,
    projectWithoutPrevLine: projectWithoutPrev,
  },
  {
    label: 'app.anomalydetails.dominostatus.owneracceptedaction',
    ...actionAccepted,
    projectWithoutPrev,
    projectWithoutPrevLine: projectWithoutPrev,
  },
  {
    label: 'app.anomalydetails.dominostatus.reviewcompleted',
    ...closed,
  },
];

export default DOMINO_STATUS;
